"use client";

import { useEffect } from "react";
import { AlertCircle } from "lucide-react";
import { PromptAttachments } from "@/components/prompt-attachments";
import { cn } from "@/lib/utils";

const CHAT_BUBBLE_LIFT_STYLE_ID = "panda-sticky-bar-chat-lift";
// 54px bar row + 12px gap; the max() term tracks the bar's own
// safe-area-aware bottom padding so the bubble always clears it.
const CHAT_BUBBLE_LIFT_CSS = `@media (max-width: 639px) {
  .lc_text-widget,
  .lc_text-widget--bubble {
    bottom: calc(max(env(safe-area-inset-bottom), 0.75rem) + 66px) !important;
  }
}`;

/**
 * On phone widths the LeadConnector chat bubble and the sticky bar both sit
 * on the bottom edge; squeezing the bar around the bubble (the old mr-16)
 * left it cramped and misaligned (PANDA-993). Instead, lift the bubble just
 * above the bar so each keeps its full footprint. The widget renders into an
 * open shadow root, so the override has to be injected inside it. The loader
 * attaches <chat-widget> (and later its shadow root — attachShadow fires no
 * mutation an observer could catch) well after hydration, so poll until the
 * root shows up.
 */
function useLiftChatBubbleAboveBar() {
  useEffect(() => {
    function inject(): boolean {
      const root = document.querySelector("chat-widget")?.shadowRoot;
      if (!root) return false;
      if (!root.querySelector(`#${CHAT_BUBBLE_LIFT_STYLE_ID}`)) {
        const style = document.createElement("style");
        style.id = CHAT_BUBBLE_LIFT_STYLE_ID;
        style.textContent = CHAT_BUBBLE_LIFT_CSS;
        root.appendChild(style);
      }
      return true;
    }

    let timer: ReturnType<typeof setInterval> | null = null;
    if (!inject()) {
      timer = setInterval(() => {
        if (inject() && timer) {
          clearInterval(timer);
          timer = null;
        }
      }, 500);
    }
    return () => {
      if (timer) clearInterval(timer);
      document
        .querySelector("chat-widget")
        ?.shadowRoot?.querySelector(`#${CHAT_BUBBLE_LIFT_STYLE_ID}`)
        ?.remove();
    };
  }, []);
}

// Same 4-point star as the hero "Start building" button (FreePlanIcon in
// home-client) so the two CTAs read as the same action.
function StarIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 28 28" fill="currentColor" {...props}>
      <path d="M14 0L17.9499 10.0501L28 14L17.9499 17.9499L14 28L10.0501 17.9499L0 14L10.0501 10.0501L14 0Z" />
    </svg>
  );
}

export type StickyPromptBarProps = {
  /** Slides the bar in/out; keep it false while the hero card is in view. */
  visible: boolean;
  mode: "idea" | "business";
  prompt: string;
  onPromptChange: (value: string) => void;
  businessUrl: string;
  onBusinessUrlChange: (value: string) => void;
  attachFiles: File[];
  onAttachFilesChange: (files: File[]) => void;
  busy: boolean;
  /** Error from the last submit, only when that submit came from this bar. */
  error: string | null;
  onSubmit: () => void;
};

/**
 * Action bar pinned to the bottom of the home page (Figma node 3398:92453).
 * Fully controlled: it shares the hero card's prompt/URL/file state so text
 * typed in either input shows in both, and submits through the exact same
 * handler as the hero CTA. Hidden via `visible` while the hero card is on
 * screen so the two inputs never compete in the first viewport; when shown,
 * the signup modal (z-60) and mobile menu (z-70) intentionally cover it.
 */
export function StickyPromptBar({
  visible,
  mode,
  prompt,
  onPromptChange,
  businessUrl,
  onBusinessUrlChange,
  attachFiles,
  onAttachFilesChange,
  busy,
  error,
  onSubmit,
}: StickyPromptBarProps) {
  const isIdea = mode === "idea";
  const value = isIdea ? prompt : businessUrl;
  const canSubmit = !busy && value.trim().length > 0;

  useLiftChatBubbleAboveBar();

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key !== "Enter") return;
    if (e.nativeEvent.isComposing || e.keyCode === 229) return;
    e.preventDefault();
    if (canSubmit) onSubmit();
  }

  return (
    // `inert` keeps the off-screen bar out of the tab order and away from
    // screen readers while it's hidden.
    <div
      inert={!visible || undefined}
      className={cn(
        "pointer-events-none fixed inset-x-0 bottom-0 z-50 px-3 pb-[max(env(safe-area-inset-bottom),0.75rem)] pt-2 transition-all duration-300 ease-out sm:px-6",
        visible ? "translate-y-0 opacity-100" : "translate-y-full opacity-0",
      )}
    >
      <div className="pointer-events-auto mx-auto w-full max-w-3xl">
        {error && (
          <div
            role="alert"
            className="mx-auto mb-2 flex w-fit max-w-full items-center gap-2 rounded-xl border border-rose-200 bg-rose-50/95 px-3 py-2 text-[13px] leading-snug text-rose-700 shadow-sm backdrop-blur dark:border-rose-500/30 dark:bg-[#1a0f14]/95 dark:text-rose-200"
          >
            <AlertCircle
              className="h-4 w-4 shrink-0 text-rose-500 dark:text-rose-400"
              strokeWidth={2}
            />
            <span className="truncate">{error}</span>
          </div>
        )}
        {/* Rose outline per Figma. Full width on phones too — the
            LeadConnector chat bubble is lifted above the bar (see
            useLiftChatBubbleAboveBar) instead of the bar squeezing
            around it. */}
        <div className="flex items-center gap-2 rounded-full border border-rose-300/70 bg-white/95 py-2 pl-2 pr-2 shadow-[0_8px_30px_rgba(0,0,0,0.14)] backdrop-blur-xl dark:border-rose-400/30 dark:bg-[#12131a]/95">
          {isIdea && (
            <span className="relative inline-flex shrink-0">
              {/* Shares `attachFiles` with the hero card, which owns the
                  preview strip — this bar has no room for it and renders the
                  count badge below instead. */}
              <PromptAttachments
                files={attachFiles}
                onChange={onAttachFilesChange}
                disabled={busy}
                showPreviews={false}
                buttonClassName="h-9 w-9 border-rose-300/70 bg-transparent text-rose-500 hover:bg-rose-50 hover:text-rose-600 dark:border-rose-400/30 dark:bg-transparent dark:text-rose-400 dark:hover:bg-rose-500/10"
              />
              {attachFiles.length > 0 && (
                <span
                  aria-label={`${attachFiles.length} attached`}
                  className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-panda-gradient-success px-1 text-[10px] font-semibold leading-none text-white"
                >
                  {attachFiles.length}
                </span>
              )}
            </span>
          )}
          <input
            id="sticky-prompt-input"
            type={isIdea ? "text" : "url"}
            inputMode={isIdea ? "text" : "url"}
            autoComplete="off"
            aria-label={
              isIdea
                ? "Describe the website you want to build"
                : "Your existing website URL"
            }
            placeholder={
              isIdea
                ? "Describe your idea…"
                : "Enter your website URL e.g. panda.new"
            }
            disabled={busy}
            // text-base (16px) at every width: iOS Safari auto-zooms the
            // whole page when focusing an input below 16px, which is what
            // made tapping the bar throw the layout around (PANDA-993).
            className={cn(
              "min-w-0 flex-1 bg-transparent px-2 font-[family-name:var(--font-rubik),sans-serif] text-base font-normal leading-6 text-foreground outline-none placeholder:text-muted-foreground/60 disabled:opacity-60",
              isIdea ? "caret-emerald-500" : "caret-violet-500",
            )}
            value={value}
            onChange={(e) =>
              isIdea
                ? onPromptChange(e.target.value)
                : onBusinessUrlChange(e.target.value)
            }
            onKeyDown={handleKeyDown}
          />
          {/* Phone widths get the Figma compact circular CTA (icon only);
              the label appears from sm up. */}
          <button
            type="button"
            onClick={onSubmit}
            disabled={!canSubmit}
            aria-label={isIdea ? "Start building" : "Start growing"}
            className={cn(
              // The mobile counterpart of the hero composer's submit, so it
              // carries the same success treatment (PANDA-2025).
              "inline-flex h-9 w-9 shrink-0 items-center justify-center gap-2 rounded-full bg-panda-gradient-success font-[family-name:var(--font-rubik),sans-serif] text-sm font-medium leading-6 tracking-normal text-white shadow-md shadow-emerald-900/30 transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600 focus-visible:ring-offset-2 focus-visible:ring-offset-background sm:h-auto sm:w-auto sm:px-5 sm:py-2.5 sm:text-base",
              canSubmit
                ? "hover:shadow-lg hover:shadow-emerald-900/40 hover:brightness-110"
                : "cursor-not-allowed opacity-60",
            )}
          >
            <StarIcon className="h-4 w-4 shrink-0 text-white" />
            <span className="hidden sm:inline">
              {busy ? "Starting…" : isIdea ? "Start building" : "Start growing"}
            </span>
          </button>
        </div>
      </div>
    </div>
  );
}
